import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { API, CompanyFullDTO, TargetingDTO } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { BookingService } from '../../../services/booking.service';
import { ErrorService } from '../../../services/error.service';
import { UserService } from '../../../services/user.service';
import { PermissionService } from '../../../services/permission.service';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { finalize, take } from 'rxjs/operators';
import { NavigationService } from '@ngx-seatback/utils';
import { TargetingOverviewComponent } from '../targeting-overview/targeting-overview.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { RequestClass } from '../../../services/global/RequestClass';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButton } from '../../../services/global/global-interfaces';

type SplitButtonAction = 'show' | 'edit';

@Component({
  selector: 'sb-page-targeting',
  templateUrl: './targeting.component.html',
  styleUrls: ['./targeting.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TargetingComponent extends RequestClass implements OnInit, ModalNavigationComponent {
  public readonly api = inject(API);
  public readonly util = inject(UtilService);
  public readonly navService = inject(NavigationService);
  public readonly translate = inject(TranslateService);
  public readonly bookingService = inject(BookingService);
  public readonly errorHandler = inject(ErrorService);
  public readonly userService = inject(UserService);
  public readonly permissionService = inject(PermissionService);
  private readonly modal = inject(ModalService);
  private readonly modalNavigation = inject(ModalNavigationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  @Input() isInModalContext: boolean;
  @Input() isInIntegratedContext: boolean;

  @Output() targetingSelected: EventEmitter<TargetingDTO>;

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  // global variables
  showSideBar: boolean;

  // table wrapper variables
  targetings: TargetingDTO[] = [];

  // filter variables
  selectedCompanies: CompanyFullDTO[] = [];
  selectedTargeting: TargetingDTO = null;
  editableTargeting: TargetingDTO = null;
  queryParamsParsed: boolean = false;
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  ngOnInit(): void {
    this.userService.selectedCompany.subscribe((company: CompanyFullDTO) => {
      if (company) {
        const found = this.selectedCompanies.findIndex(value => value.id === company.id);
        if (found === -1) {
          this.selectedCompanies = [...this.selectedCompanies, company]; // trigger change detection
          this.requestDebounced();
        }
      }
    });
    if (this.isInModalContext) {
      const modalData = this.modalNavigation.getCurrentNavigation().data;
      if (modalData.targeting) {
        this.selectedTargeting = modalData.targeting;
      }
    }
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ds_cm_Targeting_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          company: this.selectedCompanies && this.selectedCompanies.length > 0 ? this.util.arrayToIdArray(this.selectedCompanies) : [],
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (receivedTargetings: TargetingDTO[]) => {
          this.targetings = this.buildSplitButtons(receivedTargetings);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  selectTargeting(targeting: TargetingDTO) {
    if (this.selectedTargeting?.id === targeting.id) {
      this.selectedTargeting = null;
    } else {
      this.selectedTargeting = targeting;
      this.returnSelectedTargeting();
    }
  }

  returnSelectedTargeting() {
    if (this.isInModalContext) {
      this.modalNavigation.result(this.selectedTargeting);
    }
    if (this.isInIntegratedContext) {
      this.targetingSelected.emit(this.selectedTargeting);
    }
  }

  editTargeting(targeting: TargetingDTO) {
    this.showSideBar = false;
    this.editableTargeting = null;
    if (!targeting) {
      return;
    }
    if (targeting.id) {
      this.api
        .ds_cm_Targeting_editTargeting(targeting)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.requestDebounced();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .ds_cm_Targeting_createTargeting(targeting)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.requestDebounced();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  addNewTargeting() {
    this.editableTargeting = this.api.createDTO('TargetingDTO');
    this.showSideBar = true;
  }

  toggleEdit(targeting: TargetingDTO) {
    this.editableTargeting = targeting;
    this.showSideBar = true;
  }

  showDiurnalTargeting(selectedTargeting?: TargetingDTO) {
    this.modal
      .showNavigationModal({
        entryComponent: {
          value: TargetingOverviewComponent,
          data: { targeting: selectedTargeting },
        },
        title: this.translate.instant('lang.targeting.diurnalTargets'),
        modalOptions: { windowClass: 'bigXL' },
      })
      .then(() => {})
      .catch(() => {});
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'barChart',
        action: 'show',
        tooltip: 'lang.targeting.diurnalTargets',
      });
      if (this.permissionService.hasPermissions([this.permissionService.Permission.targeting_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          action: 'edit',
          tooltip: 'nav.network.targeting.edit',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(targeting: TargetingDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'show':
        this.showDiurnalTargeting(targeting);
        break;
      case 'edit':
        this.toggleEdit(targeting);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.selectedCompanies = filterMap.get('companies');
  }
}
